import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const BACKUP_URL = `${api.defaults.baseURL}/admin/backup`;
const box = "space-y-3 rounded-2xl border border-[#D2E0D8] bg-white p-5";

export const AdminData = () => {
  const ref = useRef();
  const [resetText, setResetText] = useState("");
  const [busy, setBusy] = useState(false);
  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (window.prompt(`Restore from "${file.name}"? This REPLACES all current data. Type RESTORE to continue.`) !== "RESTORE") return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("confirm", "RESTORE");
    setBusy(true);
    try { const { data } = await api.post("/admin/restore", fd); toast.success(`Restored: ${Object.entries(data.restored).map(([k, v]) => `${k} ${v}`).join(", ")}`); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (resetText !== "RESET") return toast.error("Type RESET in the box first.");
    window.open(BACKUP_URL, "_blank");
    if (!window.confirm("A backup file is downloading now. Delete ALL customers, bookings, complaints, chats, KYC, notifications and partners?")) return;
    setBusy(true);
    try { await api.post("/admin/reset", { confirm: "RESET" }); toast.success("All data cleared. Admin logins and materials are kept."); setResetText(""); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-4">
      <div className={box}>
        <p className="font-bold text-[#0D3B2B]">Daily news feed</p>
        <p className="text-sm text-[#4A6358]">Local waste, compliance and government news is fetched and summarised automatically every morning at 7am. Use this to refresh it now.</p>
        <button onClick={() => api.post("/admin/news/refresh").then((r) => toast.success(r.data.message)).catch((e) => toast.error(errMsg(e)))}
          className="h-11 rounded-xl border-2 border-[#0D3B2B] px-5 font-bold text-[#0D3B2B]" data-testid="data-news-refresh-btn">Refresh news now</button>
      </div>
      <div className={box}>
        <p className="font-bold text-[#0D3B2B]">Full backup</p>
        <p className="text-sm text-[#4A6358]">Downloads one file with all customers, bookings, partners, complaints, chats, KYC records, team logins and settings. Keep it safe: it contains personal data. Uploaded photos/documents stay in storage and are linked from the backup.</p>
        <a href={BACKUP_URL} className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#0D3B2B] px-5 font-bold text-white" data-testid="data-backup-btn"><Download className="h-4 w-4" />Download backup</a>
      </div>
      <div className={box}>
        <p className="font-bold text-[#0D3B2B]">Restore from backup</p>
        <p className="text-sm text-[#4A6358]">Replaces the current data with the data in a backup file. Your own admin login is always kept.</p>
        <input ref={ref} type="file" accept="application/json,.json" className="hidden" onChange={restore} data-testid="data-restore-input" />
        <button onClick={() => ref.current.click()} disabled={busy} className="inline-flex h-11 items-center gap-2 rounded-xl border-2 border-[#0D3B2B] px-5 font-bold text-[#0D3B2B] disabled:opacity-50" data-testid="data-restore-btn"><Upload className="h-4 w-4" />Choose backup file</button>
      </div>
      <div className={`${box} border-[#E05C3A]`}>
        <p className="flex items-center gap-2 font-bold text-[#991B1B]"><AlertTriangle className="h-5 w-5" />Reset all data (start fresh)</p>
        <p className="text-sm text-[#4A6358]">Deletes all customers, bookings, complaints, chats, KYC, notifications and pickup partners. Keeps admin/team logins, materials & prices and settings. A backup downloads first.</p>
        <div className="flex flex-wrap gap-2">
          <input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="Type RESET" className="h-11 w-40 rounded-xl border border-[#D2E0D8] px-3" data-testid="data-reset-input" />
          <button onClick={reset} disabled={busy || resetText !== "RESET"} className="h-11 rounded-xl bg-[#E05C3A] px-5 font-bold text-white disabled:opacity-40" data-testid="data-reset-btn">Delete everything</button>
        </div>
      </div>
    </div>
  );
};
